'use client'

import { unstable_useForkRef as useForkRef } from '@mui/utils'
import { type ComponentProps, type JSX, useEffect, useRef } from 'react'
import { useIntersectionObserver } from 'usehooks-ts'

import { type GkAirProxyServiceWebResponse } from '@redteclab/api/clients/crosssellers-proxy-service'

import { useRecommendationPreloadedSlotsPush } from '../../../ad-server'
import { useRecommendationWidgetContext } from '../RecommendationAreaProvider'

type RecommendationHorizontalSliderWidgetProps = ComponentProps<'div'> & {
  onIntersect?: () => void
  widgetData: GkAirProxyServiceWebResponse | undefined
  widgetId: string
}

export const RecommendationWidgetContainer = ({
  onIntersect,
  widgetData,
  widgetId,
  ...restProps
}: RecommendationHorizontalSliderWidgetProps): JSX.Element => {
  const { isIntersecting, ref } = useIntersectionObserver({
    freezeOnceVisible: true,
    rootMargin: '0px 0px 200px',
  })

  useEffect(() => {
    if (isIntersecting && onIntersect) {
      onIntersect()
    }
  }, [isIntersecting, onIntersect])

  const { adServerMetadata, widgetTitle } = useRecommendationWidgetContext()
  const metadataElementRef = useRef<HTMLDivElement>(null)
  useRecommendationPreloadedSlotsPush(
    // eslint-disable-next-line react-hooks/refs -- don't reference, refactor if touched
    metadataElementRef.current,
    adServerMetadata,
  )

  return (
    <div
      className="mb-5"
      data-analytics={widgetData ? 'recommendations-area' : undefined}
      data-qa-id="cross-sell-widget"
      data-qa-widget-type={widgetId}
      data-recommendations-area={widgetData ? widgetId : undefined}
      data-recommendations-area-title={widgetTitle}
      ref={useForkRef(ref, metadataElementRef)}
      {...restProps}
    />
  )
}
